import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TakeittoIcon } from '../components/TakeittoLogo';
import { toast } from 'sonner';
import {
  User, Mail, Lock, Phone, Calendar, Camera, Car, CreditCard,
  ChevronLeft, ChevronRight, CheckCircle2, ShieldCheck, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = [
  { value: 'car', label: 'Car' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'cargo_bike', label: 'Cargo Bike' },
  { value: 'van', label: 'Van' },
];
const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'ACT', 'TAS', 'NT'];

const STEPS = [
  { id: 1, label: 'Personal', icon: User },
  { id: 2, label: 'Vehicle', icon: Car },
  { id: 3, label: 'Licence', icon: CreditCard },
];

export default function DriverSignup() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const fileRef = useRef(null);

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '', date_of_birth: '',
    profile_photo: null,
    vehicle_type: '', vehicle_make_model: '', vehicle_plate: '', vehicle_colour: '',
    licence_number: '', licence_state: '', licence_expiry: '',
  });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    set('profile_photo', file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const ageFrom = (dob) => {
    if (!dob) return null;
    const d = new Date(dob);
    const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
    return a;
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.name || !form.email || !form.password || !form.phone || !form.date_of_birth) {
        toast.error('Please fill in all personal details');
        return false;
      }
      if (form.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return false;
      }
      const age = ageFrom(form.date_of_birth);
      if (age !== null && age < 18) {
        toast.error('You must be at least 18 years old to sign up as a driver');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!form.vehicle_type || !form.vehicle_make_model || !form.vehicle_plate || !form.vehicle_colour) {
        toast.error('Please complete all vehicle details');
        return false;
      }
      return true;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    if (!form.licence_number || !form.licence_state || !form.licence_expiry) {
      toast.error('Please complete all licence details');
      return;
    }
    setIsLoading(true);
    try {
      const fd = new FormData();
      const fields = ['name', 'email', 'password', 'phone', 'date_of_birth',
        'vehicle_type', 'vehicle_make_model', 'vehicle_plate', 'vehicle_colour',
        'licence_number', 'licence_state', 'licence_expiry'];
      fields.forEach((f) => fd.append(f, form[f]));
      if (form.profile_photo) fd.append('profile_photo', form.profile_photo);

      const res = await axios.post(`${API}/drivers/register`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { token, user } = res.data;
      setSession(token, user);
      toast.success('Account created! Complete your onboarding to go live.');
      navigate('/driver');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputWrap = 'relative';
  const iconCls = 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400';

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="driver-signup-page">
      {/* Left branding */}
      <div
        className="hidden lg:flex lg:w-2/5 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)' }}
      >
        <div className="relative z-10 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <TakeittoIcon size={44} className="opacity-90" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Takeitto</h1>
              <p className="text-sm text-white/70">Driver Sign-Up</p>
            </div>
          </div>
          <div className="space-y-5">
            <h2 className="text-4xl font-bold leading-tight">
              Drive with<br /><span className="text-cyan-300">Takeitto.</span>
            </h2>
            <p className="text-white/80 max-w-sm">
              Join our fleet of delivery partners. Set your own hours, get smart
              route optimization and reliable payouts.
            </p>
            <ul className="space-y-3 text-sm text-white/90">
              <li className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-cyan-300" /> Fast, compliant onboarding</li>
              <li className="flex items-center gap-2"><Car className="w-5 h-5 text-cyan-300" /> Car, motorcycle, cargo bike or van</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /> Get paid straight to your bank</li>
            </ul>
          </div>
          <p className="text-sm text-white/50">Powered by HERE Maps</p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-lg">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center gap-1.5" data-testid={`step-indicator-${s.id}`}>
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
                      done ? 'bg-emerald-500 text-white' : active ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs font-medium ${active ? 'text-indigo-600' : 'text-gray-400'}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 -mt-5 ${step > s.id ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl shadow-xl border p-6 sm:p-8">
            {/* Step 1: Personal */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-personal">
                <div className="mb-2">
                  <h3 className="text-xl font-bold text-gray-900">Personal Details</h3>
                  <p className="text-sm text-gray-500">Your legal name is required for compliance and payments.</p>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-20 h-20 rounded-full border-2 border-dashed border-indigo-300 flex items-center justify-center overflow-hidden bg-indigo-50 hover:bg-indigo-100 transition-colors shrink-0"
                    data-testid="profile-photo-btn"
                  >
                    {photoPreview ? (
                      <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-6 h-6 text-indigo-400" />
                    )}
                  </button>
                  <div className="text-sm text-gray-500">
                    <p className="font-medium text-gray-700">Profile photo</p>
                    <p>Shown on the dispatch map so merchants can identify you at their door.</p>
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="profile-photo-input" />
                </div>

                <div className="space-y-2">
                  <Label>Full legal name</Label>
                  <div className={inputWrap}>
                    <User className={iconCls} />
                    <Input className="pl-10" placeholder="e.g. Jordan Michael Chen" value={form.name}
                      onChange={(e) => set('name', e.target.value)} data-testid="signup-name-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email address</Label>
                  <div className={inputWrap}>
                    <Mail className={iconCls} />
                    <Input type="email" className="pl-10" placeholder="you@example.com" value={form.email}
                      onChange={(e) => set('email', e.target.value)} data-testid="signup-email-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Password</Label>
                  <div className={inputWrap}>
                    <Lock className={iconCls} />
                    <Input type="password" className="pl-10" placeholder="••••••••" value={form.password}
                      onChange={(e) => set('password', e.target.value)} data-testid="signup-password-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mobile number</Label>
                    <div className={inputWrap}>
                      <Phone className={iconCls} />
                      <Input className="pl-10" placeholder="+61 400 000 000" value={form.phone}
                        onChange={(e) => set('phone', e.target.value)} data-testid="signup-phone-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Date of birth</Label>
                    <div className={inputWrap}>
                      <Calendar className={iconCls} />
                      <Input type="date" className="pl-10" value={form.date_of_birth}
                        onChange={(e) => set('date_of_birth', e.target.value)} data-testid="signup-dob-input" />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400">You must be 18 or over. Used for background checks.</p>
              </div>
            )}

            {/* Step 2: Vehicle */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-vehicle">
                <div className="mb-2">
                  <h3 className="text-xl font-bold text-gray-900">Vehicle Details</h3>
                  <p className="text-sm text-gray-500">Your vehicle type affects which orders you can take.</p>
                </div>

                <div className="space-y-2">
                  <Label>Vehicle type</Label>
                  <Select value={form.vehicle_type} onValueChange={(v) => set('vehicle_type', v)}>
                    <SelectTrigger data-testid="signup-vehicle-type-select"><SelectValue placeholder="Select vehicle type" /></SelectTrigger>
                    <SelectContent>
                      {VEHICLE_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Make and model</Label>
                  <Input placeholder="e.g. Toyota Corolla" value={form.vehicle_make_model}
                    onChange={(e) => set('vehicle_make_model', e.target.value)} data-testid="signup-vehicle-model-input" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Registration plate</Label>
                    <Input placeholder="e.g. ABC 123" value={form.vehicle_plate}
                      onChange={(e) => set('vehicle_plate', e.target.value)} data-testid="signup-vehicle-plate-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Vehicle colour</Label>
                    <Input placeholder="e.g. White" value={form.vehicle_colour}
                      onChange={(e) => set('vehicle_colour', e.target.value)} data-testid="signup-vehicle-colour-input" />
                  </div>
                </div>
                <p className="text-xs text-gray-400">Plate and colour help customers identify you on the tracking page.</p>
              </div>
            )}

            {/* Step 3: Licence */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-licence">
                <div className="mb-2">
                  <h3 className="text-xl font-bold text-gray-900">Licence &amp; Compliance</h3>
                  <p className="text-sm text-gray-500">Required for insurance and the customer-facing tracking page.</p>
                </div>

                <div className="space-y-2">
                  <Label>Driver's licence number</Label>
                  <Input placeholder="Licence number" value={form.licence_number}
                    onChange={(e) => set('licence_number', e.target.value)} data-testid="signup-licence-number-input" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Licence state</Label>
                    <Select value={form.licence_state} onValueChange={(v) => set('licence_state', v)}>
                      <SelectTrigger data-testid="signup-licence-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent>
                        {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Licence expiry</Label>
                    <Input type="date" value={form.licence_expiry}
                      onChange={(e) => set('licence_expiry', e.target.value)} data-testid="signup-licence-expiry-input" />
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    After sign-up we'll ask you to complete onboarding (payment details, emergency
                    contact and document uploads). Your account stays <strong>pending approval</strong> until an
                    admin verifies your details.
                  </span>
                </div>
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex items-center justify-between mt-8 gap-3">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={back} data-testid="signup-back-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              ) : <span />}
              {step < 3 ? (
                <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={next} data-testid="signup-next-btn">
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSubmit}
                  disabled={isLoading} data-testid="signup-submit-btn">
                  {isLoading ? 'Creating account...' : 'Create Driver Account'}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-500">Already have an account? </span>
            <button type="button" onClick={() => navigate('/login')}
              className="text-indigo-600 hover:underline font-medium" data-testid="signup-login-link">
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
